import { useAtom } from 'jotai';
import { useCallback, useRef } from 'react';
import { ErsteMarketGoResource, MarketNewsRequest } from '../../resources';
import { ersteMarketNewsAtom, ErsteMarketNewsState } from '../../state';
import { MarketNews } from '../../types';
import { Loadable } from '../../utils';
import { GetResourceHeader } from '../useResource';

export type UseErsteMarketNewsCommonParams = {
  ersteMarketGoResource: ErsteMarketGoResource;
  getResourceHeader: GetResourceHeader;
};

const PAGE_SIZE = 20;
const FIRST_PAGE = 1;

export type UseErsteMarketNewsCommonOutput = {
  news: ErsteMarketNewsState;
  load(): Promise<void>;
  loadMore(): Promise<void>;
};

export const useErsteMarketNewsCommon = ({
  ersteMarketGoResource,
  getResourceHeader,
}: UseErsteMarketNewsCommonParams): UseErsteMarketNewsCommonOutput => {
  const [{ news, hasMore, currentPage }, setState] = useAtom(ersteMarketNewsAtom);
  const isLoadingRef = useRef(false);

  const getMarketNews = useCallback(
    async (request?: MarketNewsRequest): Promise<MarketNews[]> => {
      const result = await ersteMarketGoResource.getMarketNews(request ?? {}, getResourceHeader());
      return result.data;
    },
    [ersteMarketGoResource, getResourceHeader],
  );

  const loadPage = useCallback(
    async (page: number): Promise<void> => {
      if (isLoadingRef.current) return;
      isLoadingRef.current = true;

      setState(prev => ({
        ...prev,
        news: Loadable.hasValue(prev.news) ? Loadable.loadingWith(prev.news) : Loadable.loading,
      }));

      try {
        const nextNews = await getMarketNews({ page, limit: PAGE_SIZE });
        setState(prev => ({
          ...prev,
          news: Loadable.loaded(
            page === FIRST_PAGE ? nextNews : [...Loadable.getOrElse(prev.news, [] as MarketNews[]), ...nextNews],
          ),
          currentPage: page,
          hasMore: nextNews.length === PAGE_SIZE,
        }));
      } catch (e) {
        setState(prev => ({
          ...prev,
          news: page === FIRST_PAGE ? Loadable.failed : prev.news,
        }));
      } finally {
        isLoadingRef.current = false;
      }
    },
    [getMarketNews, setState],
  );

  const load = useCallback((): Promise<void> => loadPage(FIRST_PAGE), [loadPage]);

  const loadMore = useCallback((): Promise<void> => {
    if (!hasMore) return Promise.resolve();
    return loadPage(currentPage + 1);
  }, [hasMore, currentPage, loadPage]);

  return { news, load, loadMore };
};
